import   React                          ,
       { useState                       ,
         useEffect                      ,
		 useRef                         }  from 'react';

import   classNames                        from 'classnames';
import   styles                            from './ProductSheetDetails.module.scss';

import { useTypedSelector               }  from '../../../store/useTypeSelector';
import { RootState                      }  from '../../../store/generalReducer';

import   Field                             from '../../widget/inputs/Field';
import   FieldCalendar                     from '../../widget/inputs/FieldCalendar';
import   Select                            from '../../widget/inputs/Select';
import   Toggle                            from '../../widget/inputs/Toggle';
import   RichTextEditor                    from '../../widget/inputs/RichTextEditor';
import   Trigger                           from '../../widget/actuators/Trigger';
import   WidgetFrame                       from '../../widget/navigation/WidgetFrame';
import   CardManager , { Item }            from '../../widget/navigation/CardSet';
import   Window                            from '../../widget/windows/window';
import   Label                             from '../../widget/dataDisplay/labels/Label';
import   CreationIp                        from '../../widget/dataDisplay/labels/CreationIp';
import   CollapsiblePanel                  from '../../widget/windows/CollapsiblePanel';
import   Box                               from '../../widget/windows/Box';

import { Media } from '../../../dataTypes/mediaManagment/types';

import { Product                        ,
         ProductCategory                ,
         ProductVariant                 ,
         ProductCharacteristic          ,
         ProductMedia                   ,
         ProductPrice                   ,
         initializedProduct             ,
         initializedProductVariant      ,
         initializedCharacteristic      ,
         initializedMedia               ,
         initializedPrice               }  from '../../../dataTypes/productManagment/types';

import { UserStatus                     }  from '../../../dataTypes/userManagment/types';

import {  compareProduct                }  from '../../../dataTypes/productManagment/utils';

import { convertStringToDate                ,
         convertDateToString                }  from '../../../dataTypes/timeManagment/utils';

import { deepCopy                     }  from '../../../dataTypes/dataSetManagment/utils';

import   ProductSheet_Price                    from './ProductSheet_Price';
import   ProductSheet_Media                 ,
       { ErrorMessagesForMedia              }  from './ProductSheet_Media';
import   ProductSheet_Characteristic        ,
       { ErrorMessagesForCharacteristic     }  from './ProductSheet_Characteristic';
import   ProductSheet_ProductVariant        ,
       { ErrorMessagesForVariant            }  from './ProductSheet_ProductVariant';
import   ProductCategoryManager                from "../productCategoryManagment/ProductCategoryManager";

import { WWW_URLS                           }  from '../../../backendServices/urls';
import { createProduct                      ,
         updateProduct                      ,
         getProductAndVariantExistenceCheck }  from '../../../backendServices/productManagment/api_product';
import { getProductCategories               }  from '../../../backendServices/productManagment/api_productCategory';
import { getUserStatusList                  }  from '../../../backendServices/userManagment/api';

import { ProductSheetConfig                 ,
         getProductSheetConfig              ,
         createCustomConfig                 ,
         validateFieldsWithConfig           }  from './ProductSheetConfig';

import { BroadcastingChannel                 }  from '../../../dataTypes/broadcastingManagment/types';
import { getBroadcastingChannelList          }  from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
		 
export interface ProductSheetDetailsProps {
  product?     : Product;
  productType  : string; // 'Physical Item', 'PayVideo', 'PayChannel', 'Subscription'
  config?      : Partial<ProductSheetConfig>; // Config personnalisée optionnelle
  onCreate?    : (id: number) => void;
  onEdit?      : (id: number) => void;
}

export const ProductSheetDetails = ({ product      ,
                                      productType  ,
                                      config: customConfig,
                                      onCreate     ,
                                      onEdit       }: ProductSheetDetailsProps) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const { medias         } = useTypedSelector((state: RootState) => state.mediaListReducer);

  const [ broadcastingChannelSet , setBroadcastingChannelSet ] = useState<BroadcastingChannel[]>([]);

  // ============================================================================
  // Récupérer la configuration
  // ============================================================================
  const config = customConfig
    ? createCustomConfig(productType, customConfig)
    : getProductSheetConfig(productType);

  const { fieldsConfig, characteristicTypeOptionsConfig, labels } = config;

  const isEditMode = !!product;
  const initialProduct = isEditMode ? product! : initializedProduct();
  const copiedVariants = deepCopy(initialProduct.variants);

  const [ productCategories            , setProductCategories            ] = useState<ProductCategory[]>([]);
  const [ availableUserStatus          , setAvailableUserStatus          ] = useState<UserStatus[]>([]);
  const [ categoryCreationWindowToShow , setCategoryCreationWindowToShow ] = useState(false);

  const [ fields , setFields ] = useState({
    id                              : initialProduct.id,
    uuid                            : initialProduct.uuid,
    publicRef                       : initialProduct.publicRef,
    creationDate                    : initialProduct.creationDate,
    lastUpdateDate                  : initialProduct.lastUpdateDate,
    publicPortal                    : initialProduct.publicPortal,
    active                          : initialProduct.active,
    activeScheduled                 : initialProduct.activeScheduled,
    activeStartDate                 : initialProduct.activeStartDate,
    activeEndDate                   : initialProduct.activeEndDate,
    code                            : initialProduct.code ?? '',
    name                            : initialProduct.name ?? '',
	nameVariant0                    : initialProduct.variants[0].name ?? '',
    catchword                       : initialProduct.catchword ?? '',
    requiredStatus                  : initialProduct.requiredStatus ?? 'visitor',
    requiredUserStatusId            : initialProduct.requiredUserStatusId ?? 1,
    paymentRequired                 : initialProduct.paymentRequired ?? false,
    kind                            : productType,
    description                     : initialProduct.description ?? '',
    variants                        : copiedVariants,
    characteristics                 : deepCopy(initialProduct.characteristics),
    medias                          : deepCopy(initialProduct.medias),
    categoriesIds                   : initialProduct.categoriesIds,
    variantExistence                : initialProduct.variants ? initialProduct.variants.length > 1 : false,
    associatedMediaId               : initialProduct.associatedMediaId,
    associatedBroadcastingChannelId : initialProduct.associatedBroadcastingChannelId,
    associatedUserStatusId          : initialProduct.associatedUserStatusId,
    associatedAdminResource         : initialProduct.associatedAdminResource,
  });

  const [messages, setMessages] = useState({
    code            : '',
    barcode         : '',
    name            : '',
    nameVariant0    : '',
    catchword       : '',
    categoriesIds   : '',
  });

  const [ errorMessagesForVariants        , setErrorMessagesForVariants        ] = useState<ErrorMessagesForVariant[]>([])
  const [ errorMessagesForMedias          , setErrorMessagesForMedias          ] = useState<ErrorMessagesForMedia[]>([])
  const [ errorMessagesForCharacteristics , setErrorMessagesForCharacteristics ] = useState<ErrorMessagesForCharacteristic[]>([])

  const [ fieldsAreModified , setFieldsAreModified ] = useState(false);
  const [ saved             , setSaved             ] = useState(false);
  const [ cancelation       , setCancelation       ] = useState(false);
  const [ error             , setError             ] = useState(false);
  const [           , setErrorMsg          ] = useState('');
  const [ progress          , setProgress          ] = useState(0);
  const [ saveButtonLock    , setSaveButtonLock    ] = useState(false);

  let defautURL             = `${WWW_URLS.customer_front}/produit?id=${fields.id}`;
  let productSheetId:number = 0;

  const [  , setOpenCharacteristics ] = useState(() => {
    const initialOpenCharacteristics: Record<number, boolean> = {};
    fields.characteristics.forEach((_, index) => { initialOpenCharacteristics[index] = true; });
    return initialOpenCharacteristics;
  });

  const [  , setOpenMedias ] = useState(() => {
    const initialOpenMedias: Record<number, boolean> = {};
    fields.medias.forEach((_, index) => { initialOpenMedias[index] = true; });
    return initialOpenMedias;
  });

  const [ openVariants , setOpenVariants ] = useState(() => {
    const initialOpenVariants: Record<number, boolean> = {};
    fields.variants.forEach((_, index) => { initialOpenVariants[index] = true; });
    return initialOpenVariants;
  });

  const [ openPrices , setOpenPrices ] = useState(() => {
    const initialOpenPrices: Record<number, boolean> = {};
    fields.variants.forEach((variant) => { variant.prices.forEach((_, index) => { initialOpenPrices[index] = true; }); });
    return initialOpenPrices;
  });

  const [associatedMedia, setAssociatedMedia] = useState<Media | null>(() => {
    if (initialProduct.associatedMediaId) {
      return medias.get(initialProduct.associatedMediaId) || null;
    }
    return null;
  });

  const [associatedBroadcastingChannel, setAssociatedBroadcastingChannel] = useState<BroadcastingChannel | null>(null);


  const [associatedUserStatus, setAssociatedUserStatus] = useState<UserStatus | null>(() => {
 
    if (initialProduct.associatedUserStatusId) 
	 {
      return availableUserStatus.find(status => status.id === initialProduct.associatedUserStatusId) || null;
     }
    return null;
  });
  
  // ============================================================================
  // useEffect
  // ============================================================================

  useEffect(() => {
	
    console.log (product,availableUserStatus)
	  
    const fetchData = async () => {
      try {
        const [ categoriesResult, availableUserStatusResult, broadcastingChannelResult ] = await Promise.all([
          getProductCategories(authentication.admin?.id),
          getUserStatusList(),
		   getBroadcastingChannelList(true, authentication.admin?.id),
        ]);

        if (categoriesResult.payload) {
          setProductCategories(categoriesResult.payload.sort((a, b) => a.rank - b.rank));
        }

        if (availableUserStatusResult.code === 1) {
          setAvailableUserStatus(availableUserStatusResult.payload);
        }
		
		
        if (broadcastingChannelResult.code === 1) {
          setBroadcastingChannelSet  (broadcastingChannelResult.payload);
         }
		
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setFieldsAreModified(!compareProduct(initialProduct, fields));
  }, [fields]);

  useEffect(() => {
    if (isEditMode && product) {
      setFields((prev) => {
        const updatedVariants = prev.variants.map((variant) => ({
          ...variant,
          characteristics: variant.characteristics.map((char) =>
            char.id ? char : { ...char, id: Date.now() + Math.random() }
          ),
          medias: variant.medias.map((media) =>
            media.id ? media : { ...media, id: Date.now() + Math.random() }
          ),
          prices: variant.prices.map((price) =>
            price.id ? price : { ...price, id: Date.now() + Math.random() }
          ),
          id: variant.id || Date.now() + Math.random()
        }));

        return { ...prev, variants: updatedVariants };
      });
    }
  }, []);

  useEffect(() => {
    if (fields.associatedMediaId) {
      const media = medias.get(fields.associatedMediaId);
      setAssociatedMedia(media || null);
    } else {
      setAssociatedMedia(null);
    }
  }, [fields.associatedMediaId, medias]);

  useEffect(() => {
    if (fields.associatedUserStatusId) {
      const userStatus = availableUserStatus.find(status => status.id === fields.associatedUserStatusId);
      setAssociatedUserStatus(userStatus || null);
    } else {
      setAssociatedUserStatus(null);
    }
  }, [fields.associatedUserStatusId, availableUserStatus]);

  useEffect(() => {
    if (fields.associatedBroadcastingChannelId) {
      const broadcastingChannel = broadcastingChannelSet.find(channel => channel.id === fields.associatedBroadcastingChannelId);
	  
      setAssociatedBroadcastingChannel(broadcastingChannel || null);
    } else {
      setAssociatedBroadcastingChannel(null);
    }
  }, [fields.associatedBroadcastingChannelId, broadcastingChannelSet]);

  // ============================================================================
  // Validation et sauvegarde
  // ============================================================================

  const validateProduct = () => {
    // Utiliser la validation basée sur la config
    const { isValid, messages: validationMessages } = validateFieldsWithConfig(
      fields,
      config,
      errorMessagesForVariants,
      errorMessagesForMedias,
      errorMessagesForCharacteristics
    );

    setMessages(prev => ({ ...prev, ...validationMessages }));

    // Validation des variants (si activés)
    if (fieldsConfig.showVariants) {
      let temp: ErrorMessagesForVariant[] = [];

      for (let v = 1; v < fields.variants.length; v++) {
        let errors: ErrorMessagesForVariant = {
          code: '',
          name: '',
          medias: [],
          characteristics: []
        };

        if (fieldsConfig.showVariantCode && fields.variants[v].code === '') {
          errors.code = 'Il manque la référence variant';
        }

        if (fieldsConfig.showVariantName && fields.variants[v].name === '') {
          errors.name = 'Il manque le nom';
        }

        temp.push(errors);
      }

      setErrorMessagesForVariants(temp);
    }

    // Validation des médias du variant 0
    if (fieldsConfig.showPhotos) {
      let tempMedia: ErrorMessagesForMedia[] = [];

      for (let i = 0; i < fields.variants[0].medias.length; i++) {
        let errorsMedia: ErrorMessagesForMedia = { content: '' };
        const media = fields.variants[0].medias[i];

        if (!media.fileContent && !media.publicFileName) {
          errorsMedia.content = 'Il manque le fichier';
        }

        tempMedia.push(errorsMedia);
      }

      setErrorMessagesForMedias(tempMedia);
    }

    // Validation des caractéristiques du variant 0
    if (fieldsConfig.showCharacteristics) {
      let tempCharacteristic: ErrorMessagesForCharacteristic[] = [];

      for (let i = 0; i < fields.variants[0].characteristics.length; i++) {
        let errorsCharacteric: ErrorMessagesForCharacteristic = {
          value: '',
          name: '',
        };

        const characteristic = fields.variants[0].characteristics[i];

        if (!characteristic.name || characteristic.name.trim() === '') {
          errorsCharacteric.name = 'Il manque le nom';
        }

        if (!characteristic.value || characteristic.value.trim() === '') {
          errorsCharacteric.value = 'Il manque la valeur';
        }

        tempCharacteristic.push(errorsCharacteric);
      }

      setErrorMessagesForCharacteristics(tempCharacteristic);
    }

    console.log('validateProduct - isValid=', isValid);
    return isValid;
  };

  const save = async () => {
    setProgress(0);
    setSaved(false);

    const result = isEditMode
      ? await updateProduct(fields, setProgress, authentication.admin?.id)
      : await createProduct(fields, setProgress, authentication.admin?.id);

    console.log(result);

    if (result.code === 1) {
      productSheetId = isEditMode ? fields.id : result.payload;
      setSaved(true);
      setProgress(0);
      setError(false);
      setFieldsAreModified(false);
    } else if (result.code < 1) {
      setSaved(true);
      setProgress(0);
      setError(true);
      setErrorMsg(result.comment);
    }
  };

  // ============================================================================
  // Handlers niveau produit
  // ============================================================================

  const handleActiveChange = (newValue: boolean) => {
    setFields((prev) => ({ ...prev, active: newValue }));
  };

  const handleActiveScheduledChange = (newState: boolean) => {
    setFields((prev) => ({ ...prev, activeScheduled: newState }));
  };

  const handleActiveStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFields((prev) => ({ ...prev, activeStartDate: convertStringToDate(e.target.value, 'dd/MM/yyyy HH:mm') }));
  };

  const handleActiveEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFields((prev) => ({ ...prev, activeEndDate: convertStringToDate(e.target.value, 'dd/MM/yyyy HH:mm') }));
  };

  const handlePublicPortalChange = (newState: boolean) => {
    setFields((prev) => ({ ...prev, publicPortal: newState }));
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => ({ ...prev, code: newValue }));
    testCode(newValue);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => ({ ...prev, name: newValue }));
    setMessages((prev) => ({ ...prev, name: newValue === '' ? 'Il manque le nom' : '' }));
  };

  const handleCatchwordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => ({ ...prev, catchword: newValue }));
  };

  const handlenameVariant0Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => ({ ...prev, nameVariant0: newValue }));
    setMessages((prev) => ({ ...prev, nameVariant0: newValue === '' ? 'Il manque le nom' : '' }));
  };

  const handlePaymentRequiredChange = (newValue: boolean) => {
    setFields((prev) => ({ ...prev, paymentRequired: newValue }));
    if (newValue === true && fields.variants[0].prices.length === 0) {
      handleAddPrice();
    }
  };

  const handleRequiredStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = parseInt(e.target.value);
    setFields((prev) => ({ ...prev, requiredUserStatusId: newValue }));
  };

  const handleAssociatedAdminResourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFields((prev) => ({ ...prev, associatedAdminResource: e.target.value }));
  };

  const handleDescriptionChange = (newValue: string) => {
    setFields((prev) => ({ ...prev, description: newValue }));
  };

  const testCode = async (newCode: string) => {
    let errorMessageForCode = '';

    if (newCode === '') {
      errorMessageForCode = 'Il manque la référence produit';
    } else {
      const result = await getProductAndVariantExistenceCheck('code', newCode, true);
      if (result.code === 1 && result.payload) {
        errorMessageForCode = 'Il existe déjà un produit avec cette référence';
      }
    }

    setMessages((prev) => ({ ...prev, code: errorMessageForCode }));
  };

  const testCategoriesIds = (newCategoriesIds: number[]): string => {
    return newCategoriesIds.length === 0 ? "Il faut choisir au moins une catégorie" : "";
  };

  // ============================================================================
  // Handlers variant 0 (champs communs)
  // ============================================================================

  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFields((prev) => {
      const updatedVariants = [...prev.variants];
      if (updatedVariants.length > 0) {
        updatedVariants[0] = { ...updatedVariants[0], barcode: newValue };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  const handleStockAvailableChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedVariants = [...prev.variants];
      if (updatedVariants.length > 0) {
        updatedVariants[0] = { ...updatedVariants[0], stockAvailable: parsedValue as number };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  const handleStockThresholdChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedVariants = [...prev.variants];
      if (updatedVariants.length > 0) {
        updatedVariants[0] = { ...updatedVariants[0], stockThreshold: parsedValue as number };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  const handleStockBookedChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedVariants = [...prev.variants];
      if (updatedVariants.length > 0) {
        updatedVariants[0] = { ...updatedVariants[0], stockBooked: parsedValue as number };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  const handleStockAssignedChange = (e: React.ChangeEvent<HTMLInputElement>, parsedValue: string | number | File | Date) => {
    setFields((prev) => {
      const updatedVariants = [...prev.variants];
      if (updatedVariants.length > 0) {
        updatedVariants[0] = { ...updatedVariants[0], stockAssigned: parsedValue as number };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  // ============================================================================
  // Gestion des caractéristiques (variant 0)
  // ============================================================================

// Après la déclaration de fileInputRefsMap
const characteristicRefsMap = useRef<Map<string, React.RefObject<HTMLInputElement>>>(new Map());

const getOrCreateCharacteristicRef = (charId: string | number) => {
  const key = `${charId}`;
  if (!characteristicRefsMap.current.has(key)) {
    const newRef = React.createRef<HTMLInputElement>();
    characteristicRefsMap.current.set(key, newRef);
    console.log('✅ Nouvelle ref créée pour caractéristique:', key);
  }
  return characteristicRefsMap.current.get(key)!;
};
/*
const renderCommonCharacteristicField = () => {
  console.log('🎨 Rendering characteristics, count:', fields.variants[0].characteristics.length);
  
  // ✅ Filtrer les caractéristiques marquées 'to delete' pour ne pas les afficher
  const visibleCharacteristics = fields.variants[0].characteristics
    .filter(char => char.tag !== 'to delete')
    .sort((a, b) => a.rank - b.rank);
  
  const cardItems: Item[] = visibleCharacteristics.map((char, index) => {
    const charId = `${char.id}`;
    const charRef = getOrCreateCharacteristicRef(charId);
    
    console.log(`📋 Caractéristique ${index} - ID: ${charId}, Rank: ${char.rank}, Tag: ${char.tag}`);
    
    return {
      id: charId,
      component: (
        <ProductSheet_Characteristic 
          defaultValue={defautURL}
          characteristic={char}
          errorMessages={errorMessagesForCharacteristics}
          indexInErrorMessages={index}
          title={`Caractéristique n°${index + 1}`}
          onChange={(p) => handleCharacteristicChange(p, index)}
          withCollapsiblePanel={false}
		  characteristicTypeOptionsConfig = {characteristicTypeOptionsConfig}
        />
      ),
    };
  });

  return (
    <CardManager 
      containerWidth="100%" 
      containerHeight="237px" 
      cardWidth={700} 
      cardHeight={200}
      gap={20}
      showRemove={true}
      showUpload={false}
      showLibrary={false}
      onAdd={handleAddCharacteristicFromCard} 
      onRemove={handleDeleteCharacteristicFromCard}
      onReorder={handleCharacteristicReorder}
      items={cardItems}
    />
  );
};
*/

const renderCommonCharacteristicField = () => {
  console.log('🎨 Rendering characteristics, count:', fields.variants[0].characteristics.length);
  
  const visibleCharacteristics = fields.variants[0].characteristics
    .filter(char => char.tag !== 'to delete')
    .sort((a, b) => a.rank - b.rank);
  
  const cardItems: Item[] = visibleCharacteristics.map((char, index) => {
    const charId = `${char.id}`;
    getOrCreateCharacteristicRef(charId);
    
    // Vérifier si la caractéristique a une erreur
//    const config = characteristicTypeOptionsConfig[char.kind];
    const config = char.kind ? characteristicTypeOptionsConfig[char.kind] : null;  /////////////////////////////
	
    const hasNameError = config?.requiresName && (!char.name || char.name.trim() === '');
    const hasValueError = !char.value || char.value.trim() === '';
    const hasError = hasNameError || hasValueError;
    
    console.log(`📋 Caractéristique ${index} - ID: ${charId}, Rank: ${char.rank}, Tag: ${char.tag}, hasError: ${hasError}`);
    
    return {
      id: charId,
      component: (
        <ProductSheet_Characteristic 
          defaultValue={defautURL}
          characteristic={char}
          errorMessages={errorMessagesForCharacteristics}
          indexInErrorMessages={index}
          title={`Caractéristique n°${index + 1}`}
          onChange={(p) => handleCharacteristicChange(p, index)}
          withCollapsiblePanel={false}
          characteristicTypeOptionsConfig={characteristicTypeOptionsConfig}
          hasError={hasError} // ← AJOUTER CETTE PROP
        />
      ),
    };
  });

  return (
    <CardManager 
      containerWidth="100%" 
      containerHeight="237px" 
      cardWidth={700} 
      cardHeight={200}
      gap={20}
      showRemove={true}
      showUpload={false}
      showLibrary={false}
      onAdd={handleAddCharacteristicFromCard} 
      onRemove={handleDeleteCharacteristicFromCard}
      onReorder={handleCharacteristicReorder}
      items={cardItems}
    />
  );
};

const handleCharacteristicReorder = (reorderedIds: string[]) => {
  console.log('🔄 handleCharacteristicReorder appelé avec:', reorderedIds);

  setFields(prev => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        const charMap = new Map(variant.characteristics.map(char => [`${char.id}`, char]));
        
        const reorderedCharacteristics = reorderedIds
          .map(id => charMap.get(id))
          .filter((char): char is ProductCharacteristic => char !== undefined)
          .map((char, newIndex) => {
            const newRank = newIndex + 1;
            const hasRankChanged = char.rank !== newRank;
            
            return {
              ...char,
              rank: newRank,
              tag: hasRankChanged && char.tag !== 'to create' && char.tag !== 'to delete' 
                ? 'to update' 
                : char.tag
            };
          });

        console.log('📊 Nouveaux ranks et tags:', reorderedCharacteristics.map(c => ({ 
          id: c.id, 
          rank: c.rank, 
          tag: c.tag 
        })));

        return {
          ...variant,
          characteristics: reorderedCharacteristics,
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });
};

/*
const handleAddCharacteristicFromCard = () => {
  const newCharId = `char_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  
  const currentChars = fields.variants[0].characteristics;
  const maxRank = currentChars.length > 0 
    ? Math.max(...currentChars.map(c => c.rank)) 
    : 0;
  const newRank = maxRank + 1;
  
  const newChar = {
    ...initializedCharacteristic(),
    open: true,
    id: newCharId,
    rank: newRank,
    tag: 'to create'
  };

  console.log('➕ Ajout nouvelle caractéristique, ID:', newCharId, 'Rank:', newRank, 'Tag: to create');

  setFields(prev => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        return {
          ...variant,
          characteristics: [...variant.characteristics, newChar],
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });

  setOpenCharacteristics(prev => ({
    ...prev,
    [fields.variants[0].characteristics.length]: true,
  }));

  const charRef = getOrCreateCharacteristicRef(newCharId);
  console.log('➕ Ref créée pour nouvelle caractéristique:', charRef);
};
*/

const handleAddCharacteristicFromCard = () => {
  // Vérifier si toutes les caractéristiques existantes sont valides
  if (!checkAllCharacteristicsValid()) {
    console.warn('⚠️ Impossible d\'ajouter une caractéristique : certaines caractéristiques existantes sont mal remplies');
    return; // Bloquer l'ajout
  }

  const newCharId = `char_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  
  const currentChars = fields.variants[0].characteristics;
  const maxRank = currentChars.length > 0 
    ? Math.max(...currentChars.map(c => c.rank)) 
    : 0;
  const newRank = maxRank + 1;
  
  const newChar = {
    ...initializedCharacteristic(),
    open: true,
    id: newCharId,
    rank: newRank,
    tag: 'to create'
  };

  console.log('➕ Ajout nouvelle caractéristique, ID:', newCharId, 'Rank:', newRank, 'Tag: to create');

  setFields(prev => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        return {
          ...variant,
          characteristics: [...variant.characteristics, newChar],
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });

  setOpenCharacteristics(prev => ({
    ...prev,
    [fields.variants[0].characteristics.length]: true,
  }));

  const charRef = getOrCreateCharacteristicRef(newCharId);
  console.log('➕ Ref créée pour nouvelle caractéristique:', charRef);
};


const handleDeleteCharacteristicFromCard = (key: string) => {
  console.log('🗑️ handleDeleteCharacteristicFromCard', key);

  setFields((prev) => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        return {
          ...variant,
          characteristics: variant.characteristics.map((char) => {
            if (`${char.id}` === key) {
              if (char.tag === 'to create') {
                return null;
              }
              return {
                ...char,
                tag: 'to delete'
              };
            }
            return char;
          }).filter((char): char is ProductCharacteristic => char !== null),
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });

  characteristicRefsMap.current.delete(key);
  console.log('🗑️ Caractéristique marquée pour suppression ou supprimée:', key);
};

const handleCharacteristicChange = (updatedCharacteristic: ProductCharacteristic, charIndex: number) => {
  console.log('📝 handleCharacteristicChange', updatedCharacteristic, charIndex);

  setFields((prev) => {
    const updatedVariants = [...prev.variants];

    if (updatedVariants.length > 0) {
      const updatedCharacteristics = [...updatedVariants[0].characteristics];
      const originalChar = updatedCharacteristics[charIndex];
      
      // ✅ Vérifier si la caractéristique a été modifiée
      const hasChanged = 
        originalChar.name !== updatedCharacteristic.name ||
        originalChar.value !== updatedCharacteristic.value ||
        originalChar.kind !== updatedCharacteristic.kind ||
        originalChar.unit !== updatedCharacteristic.unit;
      
      // ✅ Marquer comme 'to update' si modifié et pas déjà marqué 'to create' ou 'to delete'
      const newTag = hasChanged && originalChar.tag !== 'to create' && originalChar.tag !== 'to delete'
        ? 'to update'
        : originalChar.tag || updatedCharacteristic.tag;

      updatedCharacteristics[charIndex] = {
        ...updatedCharacteristic,
        tag: newTag
      };
      
      updatedVariants[0] = { ...updatedVariants[0], characteristics: updatedCharacteristics };
      
      console.log('📝 Caractéristique modifiée, nouveau tag:', newTag);
    }

    return { ...prev, variants: updatedVariants };
  });

  setErrorMessagesForCharacteristics((prev) => {
    const updatedErrors = [...prev];
    const nameError = !updatedCharacteristic.name?.trim() ? 'Il manque le nom' : '';
    const valueError = !updatedCharacteristic.value?.trim() ? 'Il manque la valeur' : '';
    updatedErrors[charIndex] = { name: nameError, value: valueError };
    return updatedErrors;
  });
};


const checkAllCharacteristicsValid = (): boolean => {
  return fields.variants[0].characteristics
    .filter(char => char.tag !== 'to delete')
    .every(char => {
      // Vérifier selon le type de caractéristique
//      const config = characteristicTypeOptionsConfig[char.kind];
      const config = char.kind ? characteristicTypeOptionsConfig[char.kind] : null;  /////////////////////////////

      // Si le type nécessite un nom, vérifier qu'il est rempli
      if (config?.requiresName && (!char.name || char.name.trim() === '')) {
        return false;
      }
      
      // Toujours vérifier la valeur
      if (!char.value || char.value.trim() === '') {
        return false;
      }
      
      return true;
    });
};


// ============================================================================
  // Gestion des photos (variant 0)
  // ============================================================================
// Map pour gérer les refs de chaque média
  const fileInputRefsMap = useRef<Map<string, React.RefObject<HTMLInputElement>>>(new Map());

  // Fonction pour obtenir ou créer une ref pour un média
  const getOrCreateFileInputRef = (mediaId: string | number) => {
    // Utiliser directement en string
    const key = `${mediaId}`;
    if (!fileInputRefsMap.current.has(key)) {
      const newRef = React.createRef<HTMLInputElement>();
      fileInputRefsMap.current.set(key, newRef);
      console.log('✅ Nouvelle ref créée pour média:', key);
    }
    return fileInputRefsMap.current.get(key)!;
  };

const handlePhotoReorder = (reorderedIds: string[]) => {
  console.log('🔄 handlePhotoReorder appelé avec:', reorderedIds);

  setFields(prev => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        const mediaMap = new Map(variant.medias.map(media => [`${media.id}`, media]));
        
        const reorderedMedias = reorderedIds
          .map(id => mediaMap.get(id))
          .filter((media): media is ProductMedia => media !== undefined)
          .map((media, newIndex) => {
            const newRank = newIndex + 1;
            const hasRankChanged = media.rank !== newRank;
            
            return {
              ...media,
              rank: newRank,
              // ✅ Marquer comme 'to update' si le rank a changé et que ce n'est pas déjà marqué 'to create' ou 'to delete'
              tag: hasRankChanged && media.tag !== 'to create' && media.tag !== 'to delete' 
                ? 'to update' 
                : media.tag
            };
          });

        console.log('📊 Nouveaux ranks et tags:', reorderedMedias.map(m => ({ 
          id: m.id, 
          rank: m.rank, 
          tag: m.tag 
        })));

        return {
          ...variant,
          medias: reorderedMedias,
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });
};
  
// Handler global pour définir une image comme thumbnail (tous variants)
const handleSetThumbnailGlobal = (targetMediaId: string | number, targetVariantIndex: number) => {
  console.log('⭐ handleSetThumbnailGlobal appelé pour média:', targetMediaId, 'variant:', targetVariantIndex);

  setFields((prev) => {
    const updatedVariants = prev.variants.map((variant, vIndex) => {
      return {
        ...variant,
        medias: variant.medias.map((media) => {
          // Si c'est le média ciblé dans le variant ciblé et que c'est une image
          if (vIndex === targetVariantIndex && `${media.id}` === `${targetMediaId}` && media.mediaType === 'Image') {
            const wasAlreadyThumbnail = media.asThumbnail;
            
            // Si c'était déjà le thumbnail, on ne fait rien
            if (wasAlreadyThumbnail) {
              return media;
            }
            
            // Marquer comme thumbnail
            return {
              ...media,
              asThumbnail: true,
              tag: media.tag === 'to create' ? 'to create' : 'to update'
            };
          }
          // Pour tous les autres médias images dans TOUS les variants, on retire le statut thumbnail
          else if (media.mediaType === 'Image' && media.asThumbnail) {
            return {
              ...media,
              asThumbnail: false,
              tag: media.tag === 'to create' ? 'to create' : 'to update'
            };
          }
          
          return media;
        })
      };
    });

    console.log('⭐ Nouveau state après changement de thumbnail global');
    return {
      ...prev,
      variants: updatedVariants,
    };
  });
};

// Modifier handleSetThumbnail existant pour utiliser la version globale
const handleSetThumbnail = (targetMediaId: string | number, targetVariantIndex: number = 0) => {
  handleSetThumbnailGlobal(targetMediaId, targetVariantIndex);
};
 /* 
const handleAddPhotoFromCard = () => {
  const newPhotoId = `media_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  
  const currentMedias = fields.variants[0].medias;
  const maxRank = currentMedias.length > 0 
    ? Math.max(...currentMedias.map(m => m.rank)) 
    : 0;
  const newRank = maxRank + 1;
  
  // Vérifier s'il y a déjà un thumbnail parmi les images
  const hasThumbnail = currentMedias.some(m => m.mediaType === 'Image' && m.asThumbnail);
  
  const newPhoto = {
    ...initializedMedia(),
    open: true,
    id: newPhotoId,
    rank: newRank,
    tag: 'to create',
    asThumbnail: !hasThumbnail, // ✅ Première image = thumbnail par défaut
    mediaType: 'Image' // ✅ On définit explicitement le type
  };

  console.log('➕ Ajout nouvelle photo, ID:', newPhotoId, 'Rank:', newRank, 'asThumbnail:', !hasThumbnail);

  setFields(prev => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        return {
          ...variant,
          medias: [...variant.medias, newPhoto],
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });

  setOpenMedias(prev => ({
    ...prev,
    [fields.variants[0].medias.length]: true,
  }));

  const fileInputRef = getOrCreateFileInputRef(newPhotoId);
  console.log('➕ Ref créée pour nouvelle photo:', fileInputRef);
};
*/

const handleAddPhotoFromCard = () => {
  // Vérifier si tous les médias existants sont valides
  if (!checkAllMediasValid()) {
    // Optionnel : afficher un message à l'utilisateur
    console.warn('⚠️ Impossible d\'ajouter un média : certains médias existants n\'ont pas de fichier');
    return; // Bloquer l'ajout
  }

  const newPhotoId = `media_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  
  const currentMedias = fields.variants[0].medias;
  const maxRank = currentMedias.length > 0 
    ? Math.max(...currentMedias.map(m => m.rank)) 
    : 0;
  const newRank = maxRank + 1;
  
  const hasThumbnail = currentMedias.some(m => m.mediaType === 'Image' && m.asThumbnail);
  
  const newPhoto = {
    ...initializedMedia(),
    open: true,
    id: newPhotoId,
    rank: newRank,
    tag: 'to create',
    asThumbnail: !hasThumbnail,
    mediaType: 'Image'
  };

  console.log('➕ Ajout nouvelle photo, ID:', newPhotoId, 'Rank:', newRank, 'asThumbnail:', !hasThumbnail);

  setFields(prev => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        return {
          ...variant,
          medias: [...variant.medias, newPhoto],
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });

  setOpenMedias(prev => ({
    ...prev,
    [fields.variants[0].medias.length]: true,
  }));

  const fileInputRef = getOrCreateFileInputRef(newPhotoId);
  console.log('➕ Ref créée pour nouvelle photo:', fileInputRef);
};


   // Handler pour l'upload depuis le CardManager - VERSION SIMPLIFIÉE
  const handlePhotoUpload = (itemId: string) => {
    console.log('🔵 handlePhotoUpload appelé pour itemId:', itemId);
    
    // Récupérer la ref depuis notre Map
    const fileInputRef = fileInputRefsMap.current.get(itemId);
    console.log('🔵 fileInputRef récupérée:', fileInputRef);
    console.log('🔵 fileInputRef?.current:', fileInputRef?.current);
    
    if (fileInputRef?.current) {
      console.log('✅ Click sur input file');
      fileInputRef.current.click();
    } else {
      console.error('❌ Aucune ref disponible pour itemId:', itemId);
    }
  };

  // Handler pour la bibliothèque depuis le CardManager
  const handlePhotoLibrary = (itemId: string) => {
    console.log('📚 Ouvrir la bibliothèque pour le média:', itemId);
    // TODO: Implémenter l'ouverture de la bibliothèque de médias
  };

  // renderCommonPhotoField - VERSION CONTROLLED AVEC RANK
  

const renderCommonPhotoField = () => {
  console.log('🎨 Rendering photos, count:', fields.variants[0].medias.length);
  
  const visibleMedias = fields.variants[0].medias
    .filter(media => media.tag !== 'to delete')
    .sort((a, b) => a.rank - b.rank);
  
  const cardItems: Item[] = visibleMedias.map((media, index) => {
    const mediaId = `${media.id}`;
    const fileInputRef = getOrCreateFileInputRef(mediaId);
    
    // Vérifier si le média a une erreur (pas de fichier)
    const hasError = !(media.publicFileName || media.fileContent);
    
    console.log(`📸 Photo ${index} - ID: ${mediaId}, Rank: ${media.rank}, Tag: ${media.tag}, asThumbnail: ${media.asThumbnail}, hasError: ${hasError}`);
    
    return {
      id: mediaId,
      component: (
        <ProductSheet_Media 
          media={media}
          errorMessages={errorMessagesForMedias}
          indexInErrorMessages={index}
          title={`Photo n°${index + 1}`}
          onChange={(p) => handlePhotoChange(p, index)}
          onThumbnailClick={() => handleSetThumbnail(mediaId, 0)}
          publicRef={fields.publicRef}
          withCollapsiblePanel={false}
          externalMode={true}
          showTextInput={false}
          fileInputRef={fileInputRef}
          showMediaWithLabel={false}
          showThumbnailStar={false}
          thumbnailStarInComponent={false}
          showLegend={false}
          hasError={hasError} // ← AJOUTER CETTE PROP
        />
      ),
      isThumbnail: media.asThumbnail,
      hasContent: !!(media.publicFileName || media.fileContent),
      isImage: media.mediaType === 'Image'
    };
  });

  return (
    <CardManager 
      containerWidth="100%" 
      containerHeight="335px" 
      cardWidth={325} 
      cardHeight={300}
      gap={20}
      showRemove={true}
      showUpload={true}
      showLibrary={true}
      showThumbnail={true}
      onAdd={handleAddPhotoFromCard} 
      onRemove={handleDeletePhoto}
      onUpload={handlePhotoUpload}
      onLibrary={handlePhotoLibrary}
      onThumbnail={handleSetThumbnail}
      onReorder={handlePhotoReorder}
      items={cardItems}
      fileInputRefs={fileInputRefsMap.current}
    />
  );
};


const handleDeletePhoto = (key: string) => {
  console.log('🗑️ handleDeletePhoto', key);

  setFields((prev) => {
    const updatedVariants = prev.variants.map((variant, index) => {
      if (index === 0) {
        return {
          ...variant,
          medias: variant.medias.map((media) => {
            if (`${media.id}` === key) {
              // ✅ Si le média est nouveau ('to create'), on peut le supprimer vraiment
              if (media.tag === 'to create') {
                return null; // On le supprimera ensuite avec filter
              }
              // ✅ Sinon, on le marque comme 'to delete'
              return {
                ...media,
                tag: 'to delete'
              };
            }
            return media;
          }).filter((media): media is ProductMedia => media !== null), // Supprimer les null
        };
      }
      return variant;
    });

    return {
      ...prev,
      variants: updatedVariants,
    };
  });

  // Nettoyer la ref du média supprimé
  fileInputRefsMap.current.delete(key);
  console.log('🗑️ Média marqué pour suppression ou supprimé:', key);
};
  // handlePhotoChange - inchangé
const handlePhotoChange = (updatedPhoto: ProductMedia, photoIndex: number) => {
  console.log('📝 handlePhotoChange', updatedPhoto, photoIndex);

  setFields((prev) => {
    const updatedVariants = [...prev.variants];

    if (updatedVariants.length > 0) {
      const updatedPhotos = [...updatedVariants[0].medias];
      const originalPhoto = updatedPhotos[photoIndex];
      
      const hasChanged = 
        originalPhoto.name !== updatedPhoto.name ||
        originalPhoto.fileContent !== updatedPhoto.fileContent;
      
      const newTag = hasChanged && originalPhoto.tag !== 'to create' && originalPhoto.tag !== 'to delete'
        ? 'to update'
        : originalPhoto.tag || updatedPhoto.tag;

      // ✅ S'assurer que le mediaType est 'Image' si c'est un nouvel upload
      updatedPhotos[photoIndex] = {
        ...updatedPhoto,
        tag: newTag,
        mediaType: updatedPhoto.mediaType || 'Image' // ✅ Défaut à 'Image'
      };
      
      updatedVariants[0] = { ...updatedVariants[0], medias: updatedPhotos };
      
      console.log('📝 Photo modifiée, nouveau tag:', newTag, 'mediaType:', updatedPhotos[photoIndex].mediaType);
    }

    return { ...prev, variants: updatedVariants };
  });

  setErrorMessagesForMedias((prev) => {
    const updatedErrors = [...prev];
    const contentError = !updatedPhoto.fileContent && !updatedPhoto.publicFileName ? 'Il manque le fichier' : '';
    updatedErrors[photoIndex] = { content: contentError };
    return updatedErrors;
  });
};

const checkAllMediasValid = (): boolean => {
  return fields.variants[0].medias
    .filter(media => media.tag !== 'to delete')
    .every(media => !!(media.publicFileName || media.fileContent));
};

  // ============================================================================
  // Gestion des prix (variant 0)
  // ============================================================================

  const handlePriceChange = (updatedPrice: ProductPrice, priceIndex: number) => {
    setFields((prev) => {
      const updatedVariants = [...prev.variants];
      if (updatedVariants.length > 0) {
        const updatedPrices = [...updatedVariants[0].prices];
        updatedPrices[priceIndex] = updatedPrice;
        updatedVariants[0] = { ...updatedVariants[0], prices: updatedPrices };
      }
      return { ...prev, variants: updatedVariants };
    });
  };

  const handleAddPrice = () => {
    setFields((prev) => {
      const newPrice = {
        ...initializedPrice(),
        open: true,
        id: Date.now() + Math.random()
      };

      const updatedVariants = prev.variants.map((variant, index) => {
        if (index === 0) {
          return { ...variant, prices: [...variant.prices, newPrice] };
        }
        return variant;
      });

      return { ...prev, variants: updatedVariants };
    });

    setOpenPrices((prev) => ({ ...prev, [fields.variants[0].prices.length]: true }));
  };

  const handleDeletePrice = (priceIndex: number) => {
    setFields((prev) => {
      const updatedVariants = prev.variants.map((variant, index) => {
        if (index === 0) {
          return {
            ...variant,
            prices: variant.prices.filter((_, i) => i !== priceIndex),
          };
        }
        return variant;
      });
      return { ...prev, variants: updatedVariants };
    });
  };

  // ============================================================================
  // Gestion des variants
  // ============================================================================

  const handleVariantExistenceChange = (newValue: boolean) => {
    setFields((prev) => ({ ...prev, variantExistence: newValue }));
    if (newValue && fields.variants.length === 1) {
      handleAddVariant();
    }
  };

const handleVariantChange = (updatedVariant: ProductVariant, variantIndex: number) => {
  setFields((prev) => {
    const updatedVariants = [...prev.variants];
    
    // ✅ IMPORTANT : Vérifier si un thumbnail a été changé dans ce variant
    const oldVariant = updatedVariants[variantIndex];
    const thumbnailChanged = oldVariant.medias.some((oldMedia, idx) => {
      const newMedia = updatedVariant.medias[idx];
      return newMedia && oldMedia.asThumbnail !== newMedia.asThumbnail;
    });
    
    if (thumbnailChanged) {
      // Si le thumbnail a changé dans ce variant, il faut retirer le thumbnail des autres variants
      const finalVariants = updatedVariants.map((variant, vIdx) => {
        if (vIdx === variantIndex) {
          return updatedVariant;
        } else {
          return {
            ...variant,
            medias: variant.medias.map(media => {
              if (media.mediaType === 'Image' && media.asThumbnail) {
                return {
                  ...media,
                  asThumbnail: false,
                  tag: media.tag === 'to create' ? 'to create' : 'to update'
                };
              }
              return media;
            })
          };
        }
      });
      
      return { ...prev, variants: finalVariants };
    }
    
    // Sinon, mise à jour normale
    updatedVariants[variantIndex] = updatedVariant;
    return { ...prev, variants: updatedVariants };
  });
};

  const handleAddVariant = () => {
    setFields((prev) => {
      const newIndex = prev.variants.length;
      const newVariant = {
        ...initializedProductVariant(`${newIndex}`),
        id: Date.now() + Math.random()
      };

      const updatedVariants = [...prev.variants, newVariant];
      setOpenVariants((prevOpen) => ({ ...prevOpen, [newIndex]: true }));

      return { ...prev, variants: updatedVariants };
    });
  };

  const handleDeleteVariant = (index: number) => {
    setFields((prev) => {
      const updatedVariants = prev.variants.filter((_, i) => i !== index);
      return { ...prev, variants: updatedVariants };
    });
  };

  // ============================================================================
  // Gestion des catégories
  // ============================================================================

  const handleCategoryAddingClick = () => {
    setCategoryCreationWindowToShow(true);
  };

  const handleCategoryCreationWindowClose = () => {
    const categoriesUpdate = async () => {
      const categoriesResult = await getProductCategories(authentication.admin?.id);
      if (categoriesResult.code === 1) {
        setProductCategories(categoriesResult.payload.sort((a, b) => a.rank - b.rank));
      }
    };

    categoriesUpdate();
    setCategoryCreationWindowToShow(false);
  };

  const handleCategoryCreated = (createdCategory: ProductCategory) => {
    const categoriesUpdate = async () => {
      const categoriesResult = await getProductCategories(authentication.admin?.id);
      if (categoriesResult.code === 1) {
        setProductCategories(categoriesResult.payload.sort((a, b) => a.rank - b.rank));
      }
    };

    categoriesUpdate();
    handleChoosenCategoryChange(true, createdCategory.id);
    setCategoryCreationWindowToShow(false);
  };

  const handleChoosenCategoryChange = (newState: boolean, id: number) => {
    setFields((prevFields) => {
      const categoriesIds = prevFields.categoriesIds;
      let temp: number[] = [];

      if (newState) {
        temp = [...categoriesIds, id].filter((value, index, self) => self.indexOf(value) === index);
      } else {
        temp = categoriesIds.filter((categoryId) => categoryId !== id);
      }

      setMessages((prev) => ({ ...prev, categoriesIds: testCategoriesIds(temp) }));

      return { ...prevFields, categoriesIds: temp };
    });
  };

  // ============================================================================
  // Render helpers
  // ============================================================================

  const renderCategories = (): JSX.Element => {
    // Une seule grille (toggle + nom dans la même rangée) : deux colonnes indépendantes
    // se décalaient dès que la hauteur du toggle différait de celle du texte.
    const categoryRows: JSX.Element[] = [];
    for (let l = 0; l < productCategories.length; l++) {
      categoryRows.push(
        <div key={`toggle-${productCategories[l].id}`}>
          <Toggle onChange={(e) => handleChoosenCategoryChange(e, productCategories[l].id)}
                  checked={fields.categoriesIds.includes(productCategories[l].id)} />
        </div>
      );
      categoryRows.push(<div className={classNames(styles.name)} key={`name-${productCategories[l].id}`}>{productCategories[l].name}</div>);
    }

    return (
      <div className={classNames(styles.categoryWrapper)}>
        <div className={classNames(styles.panelWrapper)}>
          <CollapsiblePanel title="Catégories">
            <div className={classNames(styles.categories_list)}>
              {categoryRows}
            </div>
          </CollapsiblePanel>
        </div>
        <div className={styles.triggerWrapper}>
          <Trigger iconName="plus" iconWidth={15} iconHeight={15} onClick={handleCategoryAddingClick} />
        </div>
      </div>
    );
  };

  const renderRequiredUserStatus = (): JSX.Element => {
    const requiredStatusLabels = availableUserStatus.map(us => us.name);
    const requiredStatusOptions = availableUserStatus.map(us => us.id.toString());

    return (
      <Select choices={requiredStatusOptions}
              choicesLabel={requiredStatusLabels}
              value={`${fields.requiredUserStatusId}`}
              onChange={handleRequiredStatusChange} />
    );
  };

  const ADMIN_RESOURCE_CHOICES = ['disk', 'devices', 'channels', 'broadcasting', 'recording'];
  const ADMIN_RESOURCE_LABELS  = ['Disque', 'Devices', 'Chaînes', 'Diffusion', 'Enregistrement'];

  const renderAssociatedAdminResource = (): JSX.Element => {
    return (
      <Select choices={ADMIN_RESOURCE_CHOICES}
              choicesLabel={ADMIN_RESOURCE_LABELS}
              value={fields.associatedAdminResource ?? ''}
              onChange={handleAssociatedAdminResourceChange} />
    );
  };

  const renderVariantField = () => {
    return (
      <>
        <Toggle checked={fields.variantExistence}
                onChange={handleVariantExistenceChange}
                form="switch" />
        {fields.variantExistence && (
          <div className={classNames(styles.bloc)}>
            {fields.variants.slice(1).map((variant, index) => (
              <div key={variant.id} className={classNames(styles.panelWrapper)}>
                <ProductSheet_ProductVariant
  errorMessages={errorMessagesForVariants}
  indexInErrorMessages={index}
  productVariant={variant}
  config={config}
  title={`Variant n°${index+1}`}
  onChange={(v) => handleVariantChange(v, index+1)}
  onDelete={() => handleDeleteVariant(index+1)}
  onSetThumbnail={(mediaId) => handleSetThumbnailGlobal(mediaId, index+1)} // ✅ AJOUTER
  open={openVariants[index] ?? undefined}
  isEditMode={isEditMode}
  publicRef={fields.publicRef} 
/>
              </div>
            ))}
            <Trigger name="Ajouter un variant" onClick={handleAddVariant} variant="tailwind2" />
          </div>
        )}
      </>
    );
  };

  // ============================================================================
  // Handlers boutons
  // ============================================================================

  const handleSaveButton = () => {
    if (!saveButtonLock) {
      if (validateProduct()) {
        setSaveButtonLock(true);
        save();
      }
    }
  };

  const handleCreationAckButton = () => {
    setSaved(false);
    setProgress(0);
    setSaveButtonLock(false);

    if (isEditMode) {
      if (onEdit) onEdit(productSheetId);
    } else {
      if (onCreate) onCreate(productSheetId);
    }
  };

  const handleErrorAckButton = () => {
    setSaved(false);
    setProgress(0);
    setSaveButtonLock(false);
    window.scroll(0, 0);
  };

  const handleCancelButton2 = () => {
    setCancelation(false);
    setSaved(false);
    setProgress(0);
    setSaveButtonLock(false);
    window.scroll(0, 0);
  };

  const handleValidButton2 = () => {
    setCancelation(false);
    if (isEditMode) {
      if (onEdit) onEdit(0);
    } else {
      if (onCreate) onCreate(0);
    }
  };

  const handleCancelButton3 = () => {
    setCancelation(true);
  };

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  return (
    <div className={classNames(styles.root)}>

      {categoryCreationWindowToShow && (
        <Box width="1150px" maxHeight="700px" onClose={handleCategoryCreationWindowClose} marginTop="0px">
          <ProductCategoryManager onCategoryCreated={handleCategoryCreated}/>
        </Box>
      )}

      {!saved && (progress > 0) && (
        <Window message={<>Les éléments de la fiche sont en cours d'upload<br/>{`${progress}%`} effectués</>}
                messageType="information" />
      )}

      {saved && error && (
        <Window message={<>Une erreur est survenue lors de la création de la fiche</>}
                messageType="information"
                onClickOnValidateButton={handleErrorAckButton}
                validButtonText="Ok" />
      )}

      {saved && !error && (
        <Window message={isEditMode ? <>Fiche modifiée</> : <>Fiche créée</>}
                messageType="information"
                onClickOnValidateButton={handleCreationAckButton}
                validButtonText="Ok"/>
      )}

      {cancelation && (
        <Window message={<>Etes-vous sûr de vouloir annuler vos saisies ?</>}
                messageType="question"
                onClickOnCancelButton={handleCancelButton2}
                onClickOnValidateButton={handleValidButton2}
                validButtonText="Oui"
                cancelButtonText="Non" />
      )}

      <div className={classNames(styles.contain)}>

        {/* ============================================ */}
        {/* INFORMATIONS DE BASE (Edit mode only) */}
        {/* ============================================ */}

        {isEditMode && (
          <>
            <div className={classNames(styles.productId)}>
              <WidgetFrame name="Id :"
                           error=""
                           labelTextPosition="right"
                           labelBlockPosition="left"
                           classNameGrid={classNames(styles.grid_frame)}>
                <p className={classNames(styles.text)}>{fields.id}</p>
              </WidgetFrame>
            </div>

            <div className={classNames(styles.productUuid)}>
              <WidgetFrame name="Uuid :"
                           error=""
                           labelTextPosition="right"
                           labelBlockPosition="left"
                           labelWidth="70px"
                           classNameGrid={classNames(styles.grid_frame)}>
                <p className={classNames(styles.text)}>{fields.uuid}</p>
              </WidgetFrame>
            </div>

            <div className={classNames(styles.creationDate)}>
              <WidgetFrame name="Date de création :"
                           error=""
                           labelTextPosition="right"
                           labelBlockPosition="left"
                           labelWidth="170px"
                           classNameGrid={classNames(styles.grid_frame)}>
                <Label value={`${fields.creationDate ? convertDateToString(fields.creationDate, 'dd/MM/yyyy HH:mm') : ''}`} />
              </WidgetFrame>
              <CreationIp ipAddress={initialProduct.creationIpAddress} />
            </div>

            <div className={classNames(styles.url)}>
              <WidgetFrame name="URL :"
                           labelTextPosition="right"
                           labelBlockPosition="left"
                           classNameGrid={classNames(styles.grid_frame)}>
                <a href={`${WWW_URLS.customer_front}/produit?ref=${product!.code}`}
                   target="_blank"
                   rel="noopener noreferrer"
                   className={styles.productLink}>
                   {`${WWW_URLS.customer_front}/produit?ref=${product!.code}`}
                </a>
              </WidgetFrame>
            </div>
          </>
        )}

        {/* ============================================ */}
        {/* CHAMPS NIVEAU PRODUIT */}
        {/* ============================================ */}

        <div className={classNames(styles.active)}>
          <WidgetFrame name="Actif :"
                       labelTextPosition="right"
                       labelBlockPosition="left"
                       classNameGrid={classNames(styles.grid_frame)}>
            <Toggle checked={fields.active} form="switch" onChange={handleActiveChange}/>
          </WidgetFrame>
        </div>

        {fields.active && (
          <>
            <div className={classNames(styles.activeScheduled)}>
              <Toggle form="switch"
                      checked={fields.activeScheduled}
                      textOn="Programmé"
                      textOff="Immédiat"
                      show="text"
                      fillColorOn="#6632FF"
                      fillColorOff="#6632FF"
                      internalPosition={false}
                      persistentIcons={true}
                      onChange={handleActiveScheduledChange} />
            </div>

            {fields.activeScheduled && (
              <>
                <div className={classNames(styles.activeStartDate)}>
                  <WidgetFrame name="Date de début"
                               labelTextPosition="center"
                               labelBlockPosition="bottom"
                               childrenBlockPosition="center"
                               classNameGrid={classNames(styles.grid_frame)}>
                    <FieldCalendar value={fields.activeStartDate ? convertDateToString(fields.activeStartDate, 'dd/MM/yyyy HH:mm') : ''}
                                   onChange={handleActiveStartDateChange}
                                   showHours={true}
                                   checkInput={false}
                                   width="187px"
                                   dateFormat="dd/MM/yyyy HH:mm" />
                  </WidgetFrame>
                </div>

                <div className={classNames(styles.activeEndDate)}>
                  <WidgetFrame name="Date de fin"
                               labelTextPosition="center"
                               labelBlockPosition="bottom"
                               childrenBlockPosition="center"
                               classNameGrid={classNames(styles.grid_frame)}>
                    <FieldCalendar value={fields.activeEndDate ? convertDateToString(fields.activeEndDate, 'dd/MM/yyyy HH:mm') : ''}
                                   onChange={handleActiveEndDateChange}
                                   showHours={true}
                                   checkInput={false}
                                   width="187px"
                                   dateFormat="dd/MM/yyyy HH:mm" />
                  </WidgetFrame>
                </div>
              </>
            )}
          </>
        )}

        <div className={classNames(styles.publicPortal)}>
          <WidgetFrame name="Portail public :"
                       error=""
                       labelTextPosition="right"
                       labelBlockPosition="left"
                       classNameGrid={classNames(styles.grid_frame)}>
            <Toggle checked={fields.publicPortal} form="switch" onChange={handlePublicPortalChange} />
          </WidgetFrame>
        </div>

        {fieldsConfig.showRequiredStatus && (
          <div className={classNames(styles.requiredStatus)}>
            <WidgetFrame name              = "Cible client :"
                         labelTextPosition = "right"
                         classNameGrid     = {classNames(styles.grid_frame)}>
              {renderRequiredUserStatus()}
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showCode && (
          <div className={classNames(styles.code)}>
            <WidgetFrame name="Réf produit :"
                         labelTextPosition="right"
                         error={messages.code}
                         classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" value={fields.code} onChange={handleCodeChange} />
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showName && (
          <div className={classNames(styles.name)}>
            <WidgetFrame name="Nom :"
                         labelTextPosition="right"
                         error={messages.name}
                         classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" value={fields.name} onChange={handleNameChange} />
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showCatchword && (
          <div className={classNames(styles.catchword)}>
            <WidgetFrame name="Slogan :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" value={fields.catchword} onChange={handleCatchwordChange} />
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showNameVariant0 && fields.variantExistence && (
          <div className={classNames(styles.nameVariant0)}>
            <WidgetFrame name="Premier variant :"
                         labelTextPosition="right"
                         error={messages.nameVariant0}
                         classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" value={fields.nameVariant0} onChange={handlenameVariant0Change} />
            </WidgetFrame>
          </div>
        )}

        {/* ============================================ */}
        {/* CHAMPS COMMUNS (niveau variant 0) */}
        {/* ============================================ */}

        {fieldsConfig.showBarcode && (
          <div className={classNames(styles.barcode)}>
            <WidgetFrame name="Code barre :"
                         labelTextPosition="right"
                         error={messages.barcode}
                         classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" value={fields.variants[0].barcode as string} onChange={handleBarcodeChange} />
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showPhotos && (
          <div className={classNames(styles.photos)}>
            <WidgetFrame name="Photos :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {renderCommonPhotoField()}
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showDescription && (
          <div className={classNames(styles.description)}>
            <WidgetFrame name="Description :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              <RichTextEditor value={fields.description} onChange={handleDescriptionChange} />
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showStock && (
          <div className={classNames(styles.stock)}>
            <WidgetFrame name="Stocks :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              <div className={classNames(styles.stockDetails)}>

                {fieldsConfig.showStockAvailable && (
                  <div className={classNames(styles.stockAvailable)}>
                    <WidgetFrame name="Disponible :"
                                 labelTextPosition="right"
                                 classNameGrid={classNames(styles.grid_frame2)}>
                      <Field type="number" value={fields.variants[0].stockAvailable} onChange={handleStockAvailableChange} />
                    </WidgetFrame>
                  </div>
                )}

                {fieldsConfig.showStockThreshold && (
                  <div className={classNames(styles.stockThreshold)}>
                    <WidgetFrame name="Alerte :"
                                 labelTextPosition="right"
                                 classNameGrid={classNames(styles.grid_frame2)}>
                      <Field type="number" value={fields.variants[0].stockThreshold} onChange={handleStockThresholdChange} />
                    </WidgetFrame>
                  </div>
                )}

                {fieldsConfig.showStockBooked && isEditMode && (
                  <div className={classNames(styles.stockBooked)}>
                    <WidgetFrame name="Panier :"
                                 labelTextPosition="right"
                                 classNameGrid={classNames(styles.grid_frame2)}>
                      <Field type="number" value={fields.variants[0].stockBooked} onChange={handleStockAssignedChange} />
                    </WidgetFrame>
                  </div>
                )}

                {fieldsConfig.showStockAssigned && isEditMode && (
                  <div className={classNames(styles.stockAssigned)}>
                    <WidgetFrame name="A envoyer :"
                                 labelTextPosition="right"
                                 classNameGrid={classNames(styles.grid_frame2)}>
                      <Field type="number" value={fields.variants[0].stockAssigned} onChange={handleStockBookedChange} />
                    </WidgetFrame>
                  </div>
                )}

              </div>
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showAssociatedMedia && (
          <div className={classNames(styles.associatedMedia)}>
            <WidgetFrame name="Media associé :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {associatedMedia?.name ?? "Aucun média associé"}
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showAssociatedBroadcastingChannel && (
          <div className={classNames(styles.associatedBroadcastingChannel)}>
            <WidgetFrame name="Chaîne associée :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {associatedBroadcastingChannel?.name ?? "Aucune chaîne associée"}
            </WidgetFrame>
          </div>
        )}
		
        {fieldsConfig.showAssociatedUserStatus && (
          <div className={classNames(styles.associatedUserStatus)}>
            <WidgetFrame name="Statut associé :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {associatedUserStatus?.name ?? "Aucun status associé"}
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showAssociatedAdminResource && (
          <div className={classNames(styles.associatedUserStatus)}>
            <WidgetFrame name="Ressource associée :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {renderAssociatedAdminResource()}
            </WidgetFrame>
          </div>
        )}
		
        {fieldsConfig.showPaymentRequired && (
          <div className={classNames(styles.paymentRequired)}>
            <WidgetFrame name="Paiement requis :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              <Toggle checked={fields.paymentRequired} onChange={handlePaymentRequiredChange} form="switch" />
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showPrices && fields.paymentRequired && (
          <div className={classNames(styles.prices_block)}>
            <WidgetFrame name="Tarification :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              <div className={classNames(styles.prices_list)}>
                {fields.variants[0].prices.map((price, index) => (
                  <div className={classNames(styles.panelWrapper)} key={price.id}>
                    <ProductSheet_Price price={price}
                                        title={`Tarif ${index + 1}`}
                                        onChange={(p) => handlePriceChange(p, index)}
                                        onDelete={() => handleDeletePrice(index)}
                                        open={openPrices[index] ?? undefined} />
                  </div>
                ))}
                <Trigger name="Ajouter un Tarif" onClick={handleAddPrice} variant="tailwind2" />
              </div>
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showCategories && (
          <div className={classNames(styles.categories)}>
            <WidgetFrame name="Catégories :"
                         error={messages.categoriesIds}
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {renderCategories()}
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showCharacteristics && (
          <div className={classNames(styles.characteristics)}>
            <WidgetFrame name={labels?.characteristicsLabel || "Caractéristiques :"}
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {renderCommonCharacteristicField()}
            </WidgetFrame>
          </div>
        )}

        {fieldsConfig.showVariants && (
          <div className={classNames(styles.variants)}>
            <WidgetFrame name="Variants :"
                         labelTextPosition="right"
                         classNameGrid={classNames(styles.grid_frame)}>
              {renderVariantField()}
            </WidgetFrame>
          </div>
        )}

        {/* ============================================ */}
        {/* BOUTONS D'ACTION */}
        {/* ============================================ */}

        <div className={classNames(styles.buttons)}>
          <div className={classNames(styles.bloc)}>
            <Trigger name="Annuler" onClick={handleCancelButton3} variant="tailwind2" />
            &nbsp;&nbsp;&nbsp;&nbsp;
            <Trigger name={isEditMode ? 'Enregistrer' : 'Créer'} onClick={handleSaveButton} variant="tailwind2" />
            &nbsp;&nbsp;
            {fieldsAreModified && (
              <p className={classNames(styles.alert)}>N'oubliez pas d'enregistrer vos modifications</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductSheetDetails;
